/**
 * Diálogo de inspeção de equipamento de incêndio (#363): registra uma inspeção nova ou corrige
 * uma existente. É da lista e da ficha ao mesmo tempo — quem abre decide o que fazer depois de
 * gravar (`onSaved`), inclusive fechar.
 *
 * Alvo visual: docs/superpowers/specs/2026-10-15-363-incendio-nr23-mockup.html (aba 1, modal).
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import { fireEquipmentService } from '../../../services/fireEquipmentService';
import { useAuth } from '../../../stores/authStore';
import type {
  FireEquipmentLabels,
  FireInspection,
  FireInspectionFormData,
  FireInspectionFrequency,
  FireInspectionResult,
} from '../../../types/fireEquipment';
import { addMonths, formatIsoDate, maxIsoDate, todayIso } from '../fireDates';

/** Quantos meses cada frequência soma à data-base da próxima inspeção. */
const FREQUENCY_MONTHS: Record<FireInspectionFrequency, number> = {
  MENSAL: 1,
  TRIMESTRAL: 3,
  SEMESTRAL: 6,
  ANUAL: 12,
};

const RESULTS: readonly FireInspectionResult[] = ['CONFORME', 'NAO_CONFORME'];

export interface InspectionDialogEquipment {
  id: number | string;
  identifier: string;
  location: string;
  /** Quem é o equipamento em poucas palavras ("Extintor de água 10 L"), para o subtítulo. */
  summary?: string;
  inspectionFrequency: FireInspectionFrequency;
  lastInspectionDate: string | null;
  /** Piso de reativação: antes dele a inspeção não puxa a próxima (`GET /:id`). */
  inspectionDueFrom?: string | null;
}

export interface InspectionDialogProps {
  open: boolean;
  equipment: InspectionDialogEquipment;
  /** Os rótulos de `GET /fire-equipments/types`; o diálogo não tem texto próprio de resultado. */
  labels: Pick<FireEquipmentLabels, 'result' | 'frequency'>;
  /** Com uma inspeção, o diálogo corrige em vez de registrar. */
  inspection?: FireInspection;
  /** Esc e "Cancelar". Não é chamado depois de gravar: quem abriu fecha em `onSaved`. */
  onClose: () => void;
  onSaved: (inspection: FireInspection) => void;
}

interface InspectionFormProps extends Omit<InspectionDialogProps, 'open'> {
  titleId: string;
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
}

interface InspectionFieldErrors {
  date?: string;
  performedBy?: string;
}

/** Os mesmos textos de bloqueio do servidor (`FireEquipmentValidator`), para a data e quem inspecionou. */
function validateInspectionFields(date: string, performedBy: string, today: string): InspectionFieldErrors {
  const errors: InspectionFieldErrors = {};
  if (!date) errors.date = 'Data da inspeção é obrigatória';
  else if (date > today) errors.date = 'A data da inspeção não pode ser futura';
  if (!performedBy.trim()) errors.performedBy = 'Quem realizou é obrigatório';
  return errors;
}

/** A próxima inspeção que a data digitada produziria, no texto que o desenho mostra. */
function nextInspectionPreview(
  equipment: InspectionDialogEquipment,
  frequencyLabel: string,
  typedDate: string
): string | null {
  if (!typedDate) return null;

  if (equipment.inspectionDueFrom && typedDate < equipment.inspectionDueFrom) {
    return `A próxima inspeção continua em ${formatIsoDate(equipment.inspectionDueFrom)}.`;
  }

  const base = maxIsoDate(typedDate, equipment.lastInspectionDate);
  const next = addMonths(base, FREQUENCY_MONTHS[equipment.inspectionFrequency]);
  return `A próxima inspeção ficará em ${formatIsoDate(next)} (frequência ${frequencyLabel.toLowerCase()}).`;
}

const InspectionForm: React.FC<InspectionFormProps> = ({
  equipment,
  labels,
  inspection,
  onClose,
  onSaved,
  titleId,
  submitting,
  setSubmitting,
}) => {
  const { user } = useAuth();
  const [date, setDate] = useState(inspection?.inspectionDate ?? todayIso());
  const [performedBy, setPerformedBy] = useState(inspection?.performedBy ?? user?.name ?? '');
  const [result, setResult] = useState<FireInspectionResult>(inspection?.result ?? 'CONFORME');
  const [observations, setObservations] = useState(inspection?.observations ?? '');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<InspectionFieldErrors>({});
  const today = todayIso();

  const editing = Boolean(inspection);
  const subtitle = [equipment.identifier, equipment.summary, equipment.location].filter(Boolean).join(' · ');
  const preview = editing
    ? null
    : nextInspectionPreview(equipment, labels.frequency[equipment.inspectionFrequency], date);

  const checkField = (field: keyof InspectionFieldErrors, nextDate: string, nextPerformedBy: string) => {
    const found = validateInspectionFields(nextDate, nextPerformedBy, today);
    setFieldErrors((current) => ({ ...current, [field]: found[field] }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const found = validateInspectionFields(date, performedBy, today);
    setFieldErrors(found);
    if (found.date || found.performedBy) return;

    const data: FireInspectionFormData = {
      inspectionDate: date,
      result,
      performedBy,
      observations: observations || null,
    };

    setSubmitting(true);
    setError(null);
    try {
      const saved = inspection
        ? await fireEquipmentService.updateInspection(equipment.id, inspection.id, data)
        : await fireEquipmentService.createInspection(equipment.id, data);
      onSaved(saved);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Não foi possível gravar a inspeção.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <DialogTitle id={titleId} component="h2" sx={{ pb: 0.5 }}>
        {editing ? 'Corrigir inspeção' : 'Registrar inspeção'}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" color="text.secondary">
          {subtitle}
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, pt: 1 }}>
          <TextField
            label="Data da inspeção"
            type="date"
            required
            autoFocus
            value={date}
            onChange={(event) => {
              setDate(event.target.value);
              if (fieldErrors.date) checkField('date', event.target.value, performedBy);
            }}
            onBlur={() => checkField('date', date, performedBy)}
            error={Boolean(fieldErrors.date)}
            helperText={fieldErrors.date}
            inputProps={{ max: today }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Quem inspecionou"
            required
            value={performedBy}
            onChange={(event) => {
              setPerformedBy(event.target.value);
              if (fieldErrors.performedBy) checkField('performedBy', date, event.target.value);
            }}
            onBlur={() => checkField('performedBy', date, performedBy)}
            error={Boolean(fieldErrors.performedBy)}
            helperText={fieldErrors.performedBy}
          />

          <FormControl sx={{ gridColumn: { sm: '1 / -1' } }}>
            <FormLabel id="inspecao-resultado" required>
              Resultado
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="inspecao-resultado"
              value={result}
              onChange={(event) => setResult(event.target.value as FireInspectionResult)}
            >
              {RESULTS.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio />}
                  label={labels.result[option]}
                  sx={{ minHeight: 44, mr: 3 }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <TextField
            label="Observação"
            helperText="Obrigatória quando não conforme. Ex.: lacre rompido, manômetro fora da faixa, acesso bloqueado."
            multiline
            minRows={2}
            value={observations}
            onChange={(event) => setObservations(event.target.value)}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />
        </Box>

        {preview && (
          <Alert severity="info" role="status" icon={false} sx={{ mt: 2 }}>
            {preview}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-start', gap: 1 }}>
        <Button type="submit" variant="contained" disabled={submitting} sx={{ minHeight: 44 }}>
          {editing ? 'Salvar correção' : 'Registrar inspeção'}
        </Button>
        <Button onClick={onClose} sx={{ minHeight: 44 }}>
          Cancelar
        </Button>
      </DialogActions>
    </Box>
  );
};

export const InspectionDialog: React.FC<InspectionDialogProps> = ({ open, onClose, ...rest }) => {
  const [submitting, setSubmitting] = useState(false);
  const titleId = 'inspecao-titulo';

  // O formulário só existe com o diálogo aberto: cada abertura começa do zero.
  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <InspectionForm {...rest} onClose={onClose} titleId={titleId} submitting={submitting} setSubmitting={setSubmitting} />
    </Dialog>
  );
};

export default InspectionDialog;
